import { useRef, useState, useEffect } from 'react';

import { type BoxProps, VStack, HStack, Skeleton as SkeletonBase } from '@vimeo/bokeh';

import { PlaylistGrid } from '../PlaylistGrid';

export const Skeleton = (props: BoxProps) => {
  return <SkeletonBase minH="10px" w="100%" borderRadius="xs" {...props} />;
};

export interface VideoCardSkeletonProps extends BoxProps {
  isLeadingIcon?: boolean;
  numOfLines?: number;
}

export const VideoCardSkeleton = ({
  isLeadingIcon = true,
  numOfLines = 2,
  ...rest
}: VideoCardSkeletonProps) => (
  <VStack pb="75" {...rest}>
    {/* Skeleton component does not accept aspectRatio='16/9'*/}
    <Skeleton h={0} paddingBottom="56.25%" borderRadius="md" />

    <HStack w="100%" alignItems="flex-start">
      {isLeadingIcon ? <Skeleton borderRadius="round" w="xs" h="xs" /> : null}

      <VStack flex="1" alignItems="flex-start">
        {numOfLines > 0 ? <Skeleton h="14px" w="50%" /> : null}

        {numOfLines > 1 &&
          Array.from({ length: numOfLines - 1 }).map((_, i) => (
            <Skeleton key={i} h="10px" w="30%" />
          ))}
      </VStack>
    </HStack>
  </VStack>
);

const LAYOUT_SPACING = 300;

export interface BoxesSkeletonProps extends BoxProps {
  cardWidth: string | number;
  isLeadingIcon?: boolean;
  numOfLines?: number;
}

export const CarouselSkeleton = ({
  cardWidth,
  isLeadingIcon,
  numOfLines,
  ...rest
}: BoxesSkeletonProps) => {
  const [length, setLength] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const cardWidthInt = typeof cardWidth === 'string' ? parseInt(cardWidth, 10) : cardWidth;

    setLength(Math.floor(containerWidth / cardWidthInt));
  }, [cardWidth]);

  return (
    <VStack
      ref={containerRef}
      align="flex-start"
      gap={LAYOUT_SPACING}
      py={LAYOUT_SPACING}
      bgColor="background"
      overflow="hidden"
      borderRadius="drawer"
      {...rest}
    >
      <Skeleton w="150px" h="1.875rem" />

      <HStack gap={LAYOUT_SPACING}>
        {Array.from({ length }, (_, i) => (
          <VideoCardSkeleton
            key={i}
            w={cardWidth}
            isLeadingIcon={isLeadingIcon}
            numOfLines={numOfLines}
          />
        ))}
      </HStack>
    </VStack>
  );
};

export const WhatsNextSkeleton = ({ cardWidth, ...rest }: BoxesSkeletonProps) => {
  const cardWidthInt = typeof cardWidth === 'string' ? parseInt(cardWidth, 10) : cardWidth;
  const isSmall = cardWidthInt < 730;
  return (
    <HStack
      align="flex-start"
      gap={LAYOUT_SPACING}
      p={LAYOUT_SPACING}
      bgColor="background"
      overflow="hidden"
      borderRadius="drawer"
      height="350px"
      {...rest}
    >
      {!isSmall && (
        <VStack>
          {[1, 2, 3].map((_, i) => (
            <Skeleton key={`whatsnext-skeleton-text-${i}`} w={cardWidthInt / 2} h="1.875rem" />
          ))}
        </VStack>
      )}

      <VideoCardSkeleton w={isSmall ? cardWidthInt : cardWidthInt / 2} h="350px" />
    </HStack>
  );
};

export const GridSkeleton = () => (
  <VStack align="flex-start" gap="300" p="300">
    <Skeleton w="150px" h="1.875rem" />
    <PlaylistGrid numColumns={{ base: 1, sm: 2, md: 3 }} w="100%" p="0">
      {Array.from({ length: 6 }).map((_, index) => (
        <VideoCardSkeleton key={`playlist-loading-skeleton-${index}`} numOfLines={3} />
      ))}
    </PlaylistGrid>
  </VStack>
);
